import { useCallback, useMemo, useState, type ReactNode } from "react";
import { ChevronDown, Copy, Loader2, RefreshCw } from "lucide-react";

import { EnumBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import {
  rerunStediEnrichment,
  StediRerunDialog,
  type StediRerunOptions,
} from "@/components/stedi-response-modal";
import {
  VobMarkdownEditor,
  type VobBatchTarget,
} from "@/components/vob-markdown-editor";
import {
  bvSuccessCategoryLabel,
  bvSuccessCategoryTone,
  shortCaseId,
} from "@/lib/intake-format";
import type { PatientCaseApi, PatientCasesApiResponse } from "@/types/patient-case-api";
import { cn } from "@/utils";

const BV_SECTIONS: Array<{ key: keyof NonNullable<PatientCaseApi["benefits_verification"]>; label: string }> = [
  { key: "member", label: "Member" },
  { key: "payer", label: "Payer" },
  { key: "provider", label: "Provider" },
  { key: "patient", label: "Patient" },
  { key: "prescription_details", label: "Prescription" },
  { key: "medical_billing_codes", label: "Billing codes" },
  { key: "provider_details", label: "Provider details" },
  { key: "medication", label: "Medication" },
  { key: "demographics", label: "Demographics" },
  { key: "medical_insurance", label: "Medical insurance" },
  { key: "metadata", label: "Metadata" },
];

function flattenBenefitsVerificationFields(
  bv: NonNullable<PatientCaseApi["benefits_verification"]>,
): Record<string, unknown> {
  const flat: Record<string, unknown> = {};

  for (const { key, label } of BV_SECTIONS) {
    const section = bv[key];
    if (!section || typeof section !== "object") continue;

    for (const [fieldKey, value] of Object.entries(section)) {
      if (isEmptyValue(value)) continue;
      flat[`${label} · ${formatLabel(fieldKey)}`] = value;
    }
  }

  return flat;
}

function formatLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatPrimitive(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((item) => formatPrimitive(item)).join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatDateTime(value: unknown): string {
  if (typeof value !== "string" || !value.trim()) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function isEmptyValue(value: unknown): boolean {
  if (value == null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

function FieldRow({ label, value }: { label: string; value: unknown }) {
  if (isEmptyValue(value)) return null;
  return (
    <div className="grid grid-cols-[minmax(8rem,34%)_1fr] gap-x-3 gap-y-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words font-medium">{formatPrimitive(value)}</dd>
    </div>
  );
}

function ObjectFields({
  data,
  labels,
}: {
  data?: Record<string, unknown> | null;
  labels?: Record<string, string>;
}) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-sm italic text-muted-foreground">No data</p>;
  }

  const rows = Object.entries(data)
    .map(([key, value]) => {
      if (isEmptyValue(value)) return null;
      return (
        <FieldRow
          key={key}
          label={labels?.[key] ?? formatLabel(key)}
          value={value}
        />
      );
    })
    .filter(Boolean);

  if (rows.length === 0) {
    return <p className="text-sm italic text-muted-foreground">No data</p>;
  }

  return <dl className="space-y-2">{rows}</dl>;
}

function JsonBlock({
  value,
  className,
}: {
  value: unknown;
  className?: string;
}) {
  return (
    <pre
      className={cn(
        "overflow-x-auto rounded-md border bg-muted/50 p-3 text-xs leading-relaxed",
        "whitespace-pre-wrap break-words font-mono",
        className,
      )}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function CaseDetailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-md border">
      <h3 className="border-b px-3 py-2 text-sm font-medium">{title}</h3>
      <div className="px-3 py-3">{children}</div>
    </section>
  );
}

function StediRerunButton({
  rerunning,
  onRerun,
}: {
  rerunning: boolean;
  onRerun: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="default"
      className="h-8 shrink-0 gap-1.5 px-3 text-xs"
      disabled={rerunning}
      onClick={(event) => {
        event.stopPropagation();
        onRerun();
      }}
    >
      {rerunning ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      Re-run Stedi
    </Button>
  );
}

function StediSection({ data }: { data?: Record<string, unknown> | null }) {
  const hasData = Boolean(data && Object.keys(data).length > 0);

  return hasData ? (
    <JsonBlock value={data} className="max-h-72 overflow-y-auto" />
  ) : (
    <p className="text-sm italic text-muted-foreground">No Stedi response</p>
  );
}

function StediEligibilitySection({
  data,
  rerunning,
  onRerun,
}: {
  data?: Record<string, unknown> | null;
  rerunning: boolean;
  onRerun: () => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <section className="rounded-md border">
      <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
        <button
          type="button"
          className="inline-flex min-w-0 items-center gap-2 text-left text-sm font-medium hover:text-foreground/80"
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
          <span>Stedi eligibility</span>
        </button>
        <StediRerunButton rerunning={rerunning} onRerun={onRerun} />
      </div>
      {open ? (
        <div className="px-3 py-3">
          <StediSection data={data} />
        </div>
      ) : null}
    </section>
  );
}

function PatientCaseCard({
  caseData,
  defaultOpen = false,
  onRerunStedi,
  stediRerunningCaseId,
}: {
  caseData: PatientCaseApi;
  defaultOpen?: boolean;
  onRerunStedi: (caseId: string) => void;
  stediRerunningCaseId: string | null;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bv = caseData.benefits_verification;
  const memberName = [
    bv?.member?.first_name,
    bv?.member?.last_name,
  ]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");
  const payerName =
    typeof bv?.payer?.name === "string" ? bv.payer.name.trim() : "";
  const successCategory = String(
    caseData.benefits_verification_result?.success_category ?? "",
  ).trim();

  return (
    <article className="rounded-lg border bg-card shadow-sm">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30"
        onClick={() => setOpen((value) => !value)}
      >
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold" title={caseData.id}>
              {shortCaseId(caseData.id, 16)}
            </span>
            <EnumBadge value={caseData.call_status} />
            {caseData.benefits_verification?.medical_billing_codes?.j_code ? (
              <EnumBadge
                value={String(caseData.benefits_verification.medical_billing_codes.j_code)}
                semanticTone="info"
              />
            ) : null}
            {payerName ? (
              <EnumBadge value={payerName} semanticTone="neutral" />
            ) : null}
            {successCategory && successCategory !== "none" ? (
              <EnumBadge
                value={bvSuccessCategoryLabel(successCategory)}
                semanticTone={bvSuccessCategoryTone(successCategory)}
              />
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            {memberName || "Unknown member"}
            {" · "}
            {formatDateTime(caseData.created_at)}
          </p>
        </div>
        <ChevronDown
          className={cn(
            "mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open ? (
        <div className="space-y-3 border-t px-4 py-4">
        <StediEligibilitySection
          data={caseData.stedi_eligibility_response}
          rerunning={stediRerunningCaseId === caseData.id}
          onRerun={() => onRerunStedi(caseData.id)}
        />

        <CaseDetailSection title="Overview">
          <dl className="space-y-2">
            <FieldRow label="Case ID" value={caseData.id} />
            <FieldRow label="Type" value={caseData.type} />
            <FieldRow label="Call status" value={caseData.call_status} />
            <FieldRow label="Call attempts" value={caseData.call_attempts} />
            <FieldRow label="Created" value={formatDateTime(caseData.created_at)} />
            <FieldRow label="Updated" value={formatDateTime(caseData.updated_at)} />
            <FieldRow label="Last tried" value={formatDateTime(caseData.last_tried_at)} />
          </dl>
        </CaseDetailSection>

        {bv ? (
          <CaseDetailSection title="Benefits verification">
            <ObjectFields data={flattenBenefitsVerificationFields(bv)} />
          </CaseDetailSection>
        ) : null}

        {caseData.benefits_verification_result ? (
          <CaseDetailSection title="BV result">
            <ObjectFields data={caseData.benefits_verification_result} />
          </CaseDetailSection>
        ) : null}

        {!isEmptyValue(caseData.call_details) ? (
          <CaseDetailSection title="Call details">
            <JsonBlock value={caseData.call_details} className="max-h-72 overflow-y-auto" />
          </CaseDetailSection>
        ) : null}

        {caseData.metadata && Object.keys(caseData.metadata).length > 0 ? (
          <CaseDetailSection title="Case metadata">
            <ObjectFields data={caseData.metadata} />
          </CaseDetailSection>
        ) : null}

        <CaseDetailSection title="Raw case JSON">
          <JsonBlock value={caseData} className="max-h-80 overflow-y-auto" />
        </CaseDetailSection>
        </div>
      ) : null}
    </article>
  );
}

type Props = {
  response: PatientCasesApiResponse;
  onCasesRefresh?: () => void;
};

export function PatientCasesView({ response, onCasesRefresh }: Props) {
  const [copied, setCopied] = useState(false);
  const [stediRerunDialogCaseId, setStediRerunDialogCaseId] = useState<string | null>(
    null,
  );
  const [stediRerunningCaseId, setStediRerunningCaseId] = useState<string | null>(null);
  const [stediRerunError, setStediRerunError] = useState<string | null>(null);
  const cases = useMemo(
    () =>
      [...(response.cases ?? [])].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [response.cases],
  );

  const formattedJson = useMemo(
    () => JSON.stringify(response, null, 2),
    [response],
  );
  const vobMarkdownCaseId = cases[0]?.id ?? null;

  const handleStediRerun = useCallback(
    async (caseId: string, options?: StediRerunOptions) => {
      setStediRerunError(null);
      setStediRerunningCaseId(caseId);
      try {
        await rerunStediEnrichment(caseId, options);
        setStediRerunDialogCaseId(null);
        onCasesRefresh?.();
      } catch (error) {
        setStediRerunError(
          error instanceof Error ? error.message : "Stedi rerun failed",
        );
      } finally {
        setStediRerunningCaseId(null);
      }
    },
    [onCasesRefresh],
  );

  const vobBatch = useMemo<VobBatchTarget | null>(() => {
    if (!response.patient_id || !response.j_code || !response.date) return null;
    const caseIds = cases.map((caseData) => caseData.id).filter(Boolean);
    if (caseIds.length === 0) return null;
    return {
      patient_id: response.patient_id,
      j_code: response.j_code,
      date: response.date,
      case_ids: caseIds,
    };
  }, [cases, response.date, response.j_code, response.patient_id]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {cases.length} case{cases.length === 1 ? "" : "s"}
          {response.j_code ? ` · ${response.j_code}` : ""}
          {response.date ? ` · ${response.date}` : ""}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={() => {
            void navigator.clipboard.writeText(formattedJson).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          <Copy className="h-3 w-3" />
          {copied ? "Copied" : "Copy all JSON"}
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-2">
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-1">
          <div className="space-y-3 pb-4">
            {stediRerunError ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                Stedi rerun failed: {stediRerunError}
              </p>
            ) : null}
            {cases.length === 0 ? (
              <p className="text-sm text-muted-foreground">No cases returned.</p>
            ) : (
              cases.map((caseData) => (
                <PatientCaseCard
                  key={caseData.id}
                  caseData={caseData}
                  defaultOpen
                  onRerunStedi={setStediRerunDialogCaseId}
                  stediRerunningCaseId={stediRerunningCaseId}
                />
              ))
            )}
          </div>
        </div>

        <VobMarkdownEditor
          caseId={vobMarkdownCaseId}
          batch={vobBatch}
          onUploaded={onCasesRefresh}
          className="min-h-0 overflow-hidden"
        />
      </div>

      <StediRerunDialog
        caseId={stediRerunDialogCaseId}
        open={stediRerunDialogCaseId != null}
        running={stediRerunningCaseId === stediRerunDialogCaseId}
        onOpenChange={(open) => {
          if (!open && stediRerunningCaseId !== stediRerunDialogCaseId) {
            setStediRerunDialogCaseId(null);
          }
        }}
        onConfirm={(options) => {
          if (!stediRerunDialogCaseId) return;
          void handleStediRerun(stediRerunDialogCaseId, options);
        }}
      />
    </div>
  );
}
